import { useDataExplorerAppearance } from './appearanceContext';
import { useState } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';

import type {
  DataExplorerDisplayAction,
  DataExplorerDisplayActionContext,
  DataExplorerDisplayActionMenuProps,
} from './types';
import {
  getDataExplorerMenuItemToneClassName,
  resolveDataExplorerAppearance,
} from './theme';
import { cn, resolveDataExplorerValue } from './utils';

function MoreIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    >
      <circle cx="12" cy="12" r="1" />
      <circle cx="19" cy="12" r="1" />
      <circle cx="5" cy="12" r="1" />
    </svg>
  );
}

export function DataExplorerDisplayActionMenu<TSource = unknown>({
  item,
  itemId = item?.id,
  source = item?.source,
  actions = [],
  label = 'Actions',
  triggerLabel = 'Open actions',
  triggerIcon,
  align = 'end',
  sideOffset = 6,
  appearance,
  className,
  classNames,
}: DataExplorerDisplayActionMenuProps<TSource>) {
  const [open, setOpen] = useState(false);
  const theme = resolveDataExplorerAppearance(
    useDataExplorerAppearance(appearance),
  );
  const context: DataExplorerDisplayActionContext<TSource> = {
    itemId,
    item,
    source,
  };
  const visibleActions = actions.filter(
    (action) => resolveDataExplorerValue(action.visible, context) ?? true,
  );

  if (visibleActions.length === 0) {
    return null;
  }

  return (
    <span className={cn('inline-flex', classNames?.root)}>
      <DropdownMenu.Root open={open} onOpenChange={setOpen}>
        <DropdownMenu.Trigger asChild>
          <button
            type="button"
            aria-label={triggerLabel}
            className={cn(
              theme.slots.menuTrigger,
              classNames?.trigger,
              className,
            )}
            onClick={(event) => event.stopPropagation()}
          >
            {triggerIcon ?? <MoreIcon />}
            <span className="sr-only">{triggerLabel}</span>
          </button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            align={align}
            sideOffset={sideOffset}
            className={cn(
              theme.slots.menuContent,
              classNames?.content,
            )}
            style={theme.styles.menuContent}
            onClick={(event) => event.stopPropagation()}
          >
            {label ? (
              <>
                <DropdownMenu.Label
                  className={cn(
                    theme.slots.menuLabel,
                    classNames?.label,
                  )}
                >
                  {label}
                </DropdownMenu.Label>
                <DropdownMenu.Separator
                  className={cn(
                    theme.slots.menuSeparator,
                    classNames?.separator,
                  )}
                />
              </>
            ) : null}

            {visibleActions.map((action) => {
              const disabled =
                (resolveDataExplorerValue(action.disabled, context) ?? false) ||
                (resolveDataExplorerValue(action.loading, context) ?? false);

              if (action.render) {
                return (
                  <div key={action.id}>
                    {action.separatorBefore ? (
                      <DropdownMenu.Separator
                        className={cn(
                          theme.slots.menuSeparator,
                          classNames?.separator,
                        )}
                      />
                    ) : null}
                    {action.render(context)}
                    {action.separatorAfter ? (
                      <DropdownMenu.Separator
                        className={cn(
                          theme.slots.menuSeparator,
                          classNames?.separator,
                        )}
                      />
                    ) : null}
                  </div>
                );
              }

              return (
                <div key={action.id}>
                  {action.separatorBefore ? (
                    <DropdownMenu.Separator
                      className={cn(
                        '-mx-1 my-1 h-px bg-border',
                        classNames?.separator,
                      )}
                    />
                  ) : null}
                  <DropdownMenu.Item
                    title={action.title}
                    disabled={disabled}
                    className={cn(
                      theme.slots.menuItem,
                      getDataExplorerMenuItemToneClassName(theme, action.tone),
                      classNames?.item,
                      action.className,
                    )}
                    style={theme.styles.menuItem}
                    onSelect={() => {
                      if (!action.onAction || disabled) {
                        return;
                      }
                      void action.onAction(context);
                    }}
                    onContextMenu={(event) => {
                      if (!action.onContextAction || disabled) {
                        return;
                      }
                      event.preventDefault();
                      event.stopPropagation();
                      setOpen(false);
                      void action.onContextAction(context);
                    }}
                  >
                    {action.icon ? (
                      <span
                        className={cn(
                          theme.slots.menuItemIcon,
                          classNames?.itemIcon,
                        )}
                      >
                        {action.icon}
                      </span>
                    ) : null}
                    <span
                      className={cn(
                        theme.slots.menuItemLabel,
                        classNames?.itemLabel,
                      )}
                    >
                      {action.label}
                    </span>
                  </DropdownMenu.Item>
                  {action.separatorAfter ? (
                    <DropdownMenu.Separator
                      className={cn(
                        theme.slots.menuSeparator,
                        classNames?.separator,
                      )}
                    />
                  ) : null}
                </div>
              );
            })}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </span>
  );
}
